import {
  MemoryDTO,
  MemoryType,
} from '../../../components/pages/Book/MemoryBank/memoryBankTypes';
import {
  isCharacterMemoryToken,
  stripCharacterMemoryPrefix,
} from './characterMemoryPrefix';

export type ResolveCharacterTypeValueResult =
  | { ok: true; kind: 'variable'; variableId: string }
  | { ok: true; kind: 'literal'; alias: string }
  | { ok: false; error: string };

/**
 * Resolves the value side of a command whose target is a character-type
 * Memory Bank variable (`#remember`, `#check`, `#req`, `#check CONDITIONS`).
 *
 * Two valid forms per the text-format contract:
 *   1. `memory.<name>` — another character-type Memory Bank variable
 *      (case-insensitive prefix).
 *   2. bare alias — a static CharacterSkin alias from the book.
 *
 * Returns a discriminated union: a `variable` link, a `literal` alias, or an
 * error describing why the value could not be resolved.
 */
export function resolveCharacterTypeValue(
  value: string,
  memoryBank: MemoryDTO[],
  characterNamesInBook: string[],
): ResolveCharacterTypeValueResult {
  const trimmed = value.trim();

  if (isCharacterMemoryToken(trimmed)) {
    const memoryName = stripCharacterMemoryPrefix(trimmed).toLocaleLowerCase().trim();
    const memory = memoryBank.find((m) => m.name.toLocaleLowerCase().trim() === memoryName);
    if (!memory) {
      return { ok: false, error: `CharacterMemory variable does not exist in Memory Bank: ${trimmed}` };
    }
    if (memory.type !== MemoryType.Character) {
      return { ok: false, error: `Memory variable is not of type Character: ${trimmed}` };
    }
    return { ok: true, kind: 'variable', variableId: String(memory.id) };
  }

  const aliasLc = trimmed.toLocaleLowerCase();
  const aliasMatch = characterNamesInBook.find((a) => a.toLocaleLowerCase() === aliasLc);
  if (!aliasMatch) {
    return { ok: false, error: `Value must be an existing CharacterSkin alias or a memory.-prefixed character-type Memory Bank variable: ${trimmed}` };
  }
  return { ok: true, kind: 'literal', alias: aliasMatch };
}
